import {
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import SendIcon from "@material-ui/icons/Send";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  ECABIN,
  formatTimeWithTZOffset,
  getToken,
  getUser,
  requestErrorHandler,
} from "../../Utils/Common";
import EnvSettings from "../../Utils/EnvSettings";
import CTN_logo from "../../images/ctnfi.png";
import { showPopupSnackbar } from "../../redux/actions/snackbarActions";

const server = EnvSettings.server;
const SIGNATURE_LOGO_CONTENT_ID = "ctnfi-signature-logo";
const SIGNATURE_LOGO_CID = "cid:" + SIGNATURE_LOGO_CONTENT_ID;
const SIGNATURE_LOGO_FILENAME = "ctnfi.png";
const MAX_EMAIL_ATTACHMENT_BYTES = 25 * 1024 * 1024;

const parseJsonValue = (value) => {
  if (!value || typeof value === "object") {
    return value || {};
  }
  try {
    return JSON.parse(value);
  } catch (error) {
    return {};
  }
};

const parseEmailAddress = (value) => {
  var input = String(value || "").trim();
  var match;

  if (!input) {
    return "";
  }
  match = input.match(/<([^<>@\s]+@[^<>\s]+)>/);
  if (match?.[1]) {
    return match[1].trim();
  }
  match = input.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  if (match?.[0]) {
    return match[0].trim();
  }
  return "";
};

const getContactEmailAddress = (contact) => {
  var additionalInfo = parseJsonValue(contact?.additionalInfo);
  return (
    additionalInfo.contactEmail ||
    additionalInfo.emailAddress ||
    additionalInfo.email ||
    ""
  );
};

const getTicketContactEmailAddress = (ticket) => {
  return (
    getContactEmailAddress(ticket?.Contact) ||
    getContactEmailAddress(ticket?.Device?.Contact)
  );
};

const getHeader = (message, name) => {
  var headers = message?.headers || [];
  var header;

  if (!headers.length) {
    return "";
  }
  header = headers.find(
    (item) => item.name && item.name.toLowerCase() === name.toLowerCase(),
  );
  return header?.value || "";
};

const getDisplayBody = (message) => {
  if (message.bodyText) {
    return message.bodyText;
  }
  if (message.snippet) {
    return message.snippet;
  }
  return "";
};

const escapeHtml = (value) => {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

const getTextAsHtml = (value) => {
  return escapeHtml(value).replace(/\r\n/g, "\n").replace(/\n/g, "<br />");
};

const getAbsoluteAssetUrl = (assetUrl) => {
  var value = String(assetUrl || "");

  if (!value || /^https?:\/\//i.test(value) || /^data:/i.test(value)) {
    return value;
  }
  if (typeof window === "undefined") {
    return value;
  }
  return new URL(value, window.location.origin).toString();
};

const getExternalSignatureLogoUrl = () => {
  return process.env.REACT_APP_EMAIL_SIGNATURE_LOGO_URL || "";
};

const getSignatureLogoPreviewUrl = () => {
  return getExternalSignatureLogoUrl() || getAbsoluteAssetUrl(CTN_logo);
};

const getRenderableHtml = (html) => {
  return String(html || "").replace(
    /cid:<?ctnfi-signature-logo>?/g,
    getSignatureLogoPreviewUrl(),
  );
};

const readBlobAsDataUrl = (blob) => {
  return new Promise((resolve, reject) => {
    var reader = new FileReader();

    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const getBase64FromDataUrl = (dataUrl) => {
  var value = String(dataUrl || "");
  var commaIndex = value.indexOf(",");

  if (commaIndex < 0) {
    return "";
  }
  return value.slice(commaIndex + 1);
};

const getFileAttachment = async (file) => {
  var dataUrl = await readBlobAsDataUrl(file);

  return {
    filename: file.name,
    mimeType: file.type || "application/octet-stream",
    base64: getBase64FromDataUrl(dataUrl),
    size: file.size,
  };
};

const getSignatureLogoAttachment = async () => {
  var response;
  var dataUrl;
  var base64;

  if (getExternalSignatureLogoUrl()) {
    return;
  }
  if (typeof fetch !== "function" || typeof FileReader === "undefined") {
    return;
  }

  try {
    response = await fetch(CTN_logo);
    if (!response.ok) {
      return;
    }
    dataUrl = await readBlobAsDataUrl(await response.blob());
    base64 = getBase64FromDataUrl(dataUrl);
    if (!base64) {
      return;
    }
    return {
      filename: SIGNATURE_LOGO_FILENAME,
      mimeType: "image/png",
      base64,
      contentId: SIGNATURE_LOGO_CONTENT_ID,
      inline: true,
    };
  } catch (error) {
    return;
  }
};

const getNormalizedText = (value) => {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim();
};

const isQuoteMarkerText = (value) => {
  var text = getNormalizedText(value);

  return (
    /^On .+wrote:$/i.test(text) ||
    /^-{2,}\s*Original Message\s*-{2,}$/i.test(text) ||
    /^_{5,}$/.test(text)
  );
};

const isOutlookHeaderStart = (lines, index) => {
  if (!/^From:\s+/i.test(lines[index] || "")) {
    return false;
  }
  return lines.slice(index + 1, index + 7).some((line) => {
    return /^(Sent|Date|To|Cc|Subject):\s+/i.test(line || "");
  });
};

const getTrimmedPlainText = (value) => {
  var lines = String(value || "")
    .replace(/\r\n/g, "\n")
    .split("\n");
  var quoteStartIndex = -1;

  for (let index = 0; index < lines.length; index++) {
    if (
      isQuoteMarkerText(lines[index]) ||
      /^>\s?/.test(lines[index]) ||
      isOutlookHeaderStart(lines, index)
    ) {
      quoteStartIndex = index;
      break;
    }
  }

  if (quoteStartIndex < 0) {
    return String(value || "");
  }

  return lines.slice(0, quoteStartIndex).join("\n").trimEnd();
};

const removeElementAndFollowing = (element) => {
  var parent = element?.parentNode;

  if (!parent) {
    return;
  }
  while (element.nextSibling) {
    parent.removeChild(element.nextSibling);
  }
  parent.removeChild(element);
};

const getTrimmedHtml = (html) => {
  var parser;
  var doc;
  var quoteElement;
  var quoteSelectors = [
    ".gmail_quote",
    "[class*='gmail_quote']",
    ".yahoo_quoted",
    "blockquote[type='cite']",
    ".moz-cite-prefix",
    "#divRplyFwdMsg",
    ".OutlookMessageHeader",
    "#isForwardContent",
  ];

  if (!html || typeof DOMParser === "undefined") {
    return String(html || "");
  }

  parser = new DOMParser();
  doc = parser.parseFromString(String(html), "text/html");

  quoteSelectors.forEach((selector) => {
    doc.querySelectorAll(selector).forEach((element) => {
      element.remove();
    });
  });

  quoteElement = Array.from(
    doc.body.querySelectorAll("div,p,span,blockquote,hr"),
  ).find((element) => {
    var text = getNormalizedText(element.textContent);
    return text.length < 300 && isQuoteMarkerText(text);
  });
  if (quoteElement) {
    removeElementAndFollowing(quoteElement);
  }

  return doc.body.innerHTML.trim();
};

const getMessagePreviewContent = (message, expanded) => {
  var fullHtml = String(message.bodyHtml || "");
  var fullText = getDisplayBody(message);
  var trimmedHtml;
  var trimmedText;

  if (fullHtml.trim()) {
    trimmedHtml = getTrimmedHtml(fullHtml);
    return {
      type: "html",
      value: expanded ? fullHtml : trimmedHtml,
      hasTrimmedContent:
        getNormalizedText(trimmedHtml) !== getNormalizedText(fullHtml),
    };
  }

  trimmedText = getTrimmedPlainText(fullText);
  return {
    type: "text",
    value: expanded ? fullText : trimmedText,
    hasTrimmedContent:
      getNormalizedText(trimmedText) !== getNormalizedText(fullText),
  };
};

const getFileSizeLabel = (size) => {
  var bytes = Number(size);
  var units = ["B", "KB", "MB", "GB"];
  var index = 0;

  if (!Number.isFinite(bytes) || bytes < 0) {
    return "";
  }
  while (bytes >= 1024 && index < units.length - 1) {
    bytes /= 1024;
    index += 1;
  }
  return (
    (index === 0 ? bytes.toFixed(0) : bytes.toFixed(bytes >= 10 ? 1 : 2)) +
    " " +
    units[index]
  );
};

const getAttachmentHeader = (attachment, name) => {
  var headers = attachment?.headers || [];

  if (!Array.isArray(headers)) {
    return "";
  }
  return (
    headers.find(
      (header) =>
        header.name && header.name.toLowerCase() === name.toLowerCase(),
    )?.value || ""
  );
};

const isInlineAttachment = (attachment) => {
  return Boolean(
    attachment?.inline ||
      attachment?.contentId ||
      attachment?.cid ||
      /^inline/i.test(getAttachmentHeader(attachment, "Content-Disposition")),
  );
};

const getAttachmentFilename = (attachment) => {
  return attachment?.filename || attachment?.name || "";
};

const getVisibleAttachments = (attachments) => {
  if (!Array.isArray(attachments)) {
    return [];
  }
  return attachments.filter((attachment) => {
    return getAttachmentFilename(attachment) && !isInlineAttachment(attachment);
  });
};

const getFileTotalSize = (files) => {
  return (files || []).reduce((total, file) => {
    return total + (Number(file?.size) || 0);
  }, 0);
};

const getHtmlDocument = (html) => {
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <base target="_blank" />
    <style>
      html,
      body {
        background: #ffffff;
        box-sizing: border-box;
        color: #202124;
        font-family: Arial, sans-serif;
        margin: 0;
        overflow-wrap: anywhere;
        padding: 0;
      }
      body {
        padding: 12px;
      }
      img {
        height: auto;
        max-width: 100%;
      }
      table {
        max-width: 100%;
      }
    </style>
  </head>
  <body>${html}</body>
</html>`;
};

const getMessageTimeValue = (message) => {
  if (message.receivedAt) {
    return new Date(message.receivedAt).getTime();
  }
  if (message.timestamp && Number.isFinite(Number(message.timestamp))) {
    return Number(message.timestamp);
  }
  if (message.timestamp) {
    return new Date(message.timestamp).getTime();
  }
  return new Date(message.createdAt || 0).getTime();
};

const formatMessageTime = (message) => {
  var value = getMessageTimeValue(message);

  if (!Number.isFinite(value) || !value) {
    return "";
  }
  return formatTimeWithTZOffset(new Date(value).toISOString(), ECABIN);
};

const getOpeningEmailMessage = (messages) => {
  return (
    messages.find((message) => message.direction !== "sent") || messages[0]
  );
};

const getReplyRecipient = (ticket, messages) => {
  var openingMessage = getOpeningEmailMessage(messages);

  return (
    parseEmailAddress(openingMessage?.sender) ||
    getTicketContactEmailAddress(ticket)
  );
};

const getReplySubject = (ticket, messages) => {
  var openingMessage = getOpeningEmailMessage(messages);
  var subject = openingMessage?.subject || ticket?.description || "";

  if (!subject) {
    return "";
  }
  if (/^re:/i.test(subject)) {
    return subject;
  }
  return "Re: " + subject;
};

const getNewThreadSubject = (ticket) => {
  return ticket?.description || ticket?.summary || ticket?.number || "";
};

const getLatestThreadMessage = (messages) => {
  return [...messages].reverse().find((message) => message.gmailThreadId);
};

const getReferences = (message) => {
  var references = getHeader(message, "References");
  var messageId = message?.rfcMessageId;

  if (references && messageId && references.indexOf(messageId) === -1) {
    return references + " " + messageId;
  }
  return references || messageId || "";
};

const getSortedMessages = (messages) => {
  return [...(messages || [])].sort((a, b) => {
    return getMessageTimeValue(a) - getMessageTimeValue(b);
  });
};

const getEmptyCompose = () => ({
  bodyText: "",
  subject: "",
});

const getUserFirstName = () => {
  return String(getUser()?.name || "")
    .trim()
    .split(/\s+/)[0];
};

const getTicketNumber = (ticket) => {
  return ticket?.number || ticket?.id || "";
};

const getEmailSignatureLines = (ticket) => {
  return [
    "Best Regards,",
    getUserFirstName(),
    "CTN Service Team",
    "Ticket number " + getTicketNumber(ticket),
  ].filter(Boolean);
};

const getSignedMessageBody = (bodyText, ticket) => {
  var signature = getEmailSignatureLines(ticket).join("\n");

  return String(bodyText || "").trimEnd() + "\n\n" + signature;
};

const getSignedMessageHtml = (bodyText, ticket, logoUrl) => {
  var signatureHtml = getEmailSignatureLines(ticket)
    .map((line) => escapeHtml(line))
    .join("<br />");

  return [
    '<div style="font-family: Arial, sans-serif; font-size: 14px; line-height: 1.4; color: #202124;">',
    "<div>" + getTextAsHtml(String(bodyText || "").trimEnd()) + "</div>",
    "<br />",
    "<div>",
    signatureHtml,
    logoUrl
      ? '<br /><img src="' +
        escapeHtml(logoUrl) +
        '" alt="CTN" style="display: block; margin-top: 8px; max-width: 120px; height: auto;" />'
      : "",
    "</div>",
    "</div>",
  ].join("");
};

const getMessageKey = (message) => {
  return String(message.id || message.gmailMessageId);
};

const getCollapsedHeaders = (messages) => {
  return (messages || []).reduce((collapsedHeaders, message) => {
    collapsedHeaders[getMessageKey(message)] = true;
    return collapsedHeaders;
  }, {});
};

const getGmailMessageUrl = (message) => {
  var gmailId = message?.gmailThreadId || message?.gmailMessageId;
  var mailboxEmailAddress = parseEmailAddress(message?.mailboxEmailAddress);
  var accountPath;

  if (!gmailId) {
    return "";
  }

  accountPath = mailboxEmailAddress
    ? "?authuser=" + encodeURIComponent(mailboxEmailAddress)
    : "0/";
  return (
    "https://mail.google.com/mail/u/" +
    accountPath +
    "#all/" +
    encodeURIComponent(gmailId)
  );
};

const AttachmentChips = ({ attachments, onRemove }) => {
  var visibleAttachments = getVisibleAttachments(attachments);

  if (!visibleAttachments.length) {
    return null;
  }

  return (
    <Grid container spacing={1} style={{ marginTop: 4 }}>
      {visibleAttachments.map((attachment, index) => (
        <Grid
          item
          key={getAttachmentFilename(attachment) + "-" + index}
          style={{ maxWidth: "100%" }}
        >
          <Chip
            size="small"
            icon={<AttachFileIcon fontSize="small" />}
            label={
              getAttachmentFilename(attachment) +
              (attachment.size ? " (" + getFileSizeLabel(attachment.size) + ")" : "")
            }
            onDelete={onRemove ? () => onRemove(index) : undefined}
            style={{ maxWidth: "100%" }}
          />
        </Grid>
      ))}
    </Grid>
  );
};

const MessageHeader = ({ collapsed, message, onToggle, t }) => {
  var gmailMessageUrl = getGmailMessageUrl(message);
  var visibleAttachments = getVisibleAttachments(message.attachments);
  var attachmentCount = visibleAttachments.length;

  return (
    <>
      <Grid item xs={12} container alignItems="center" spacing={1}>
        <Grid item>
          <IconButton size="small" onClick={onToggle}>
            {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Grid>
        <Grid item>
          <Chip
            size="small"
            color={message.direction === "sent" ? "primary" : "default"}
            label={
              message.direction === "sent"
                ? t("ticketList.emailSent")
                : t("ticketList.emailReceived")
            }
          />
        </Grid>
        <Grid item>
          <Typography variant="body2" color="textSecondary">
            {formatMessageTime(message)}
          </Typography>
        </Grid>
        {attachmentCount ? (
          <Grid item>
            <Chip
              size="small"
              variant="outlined"
              icon={<AttachFileIcon fontSize="small" />}
              label={
                attachmentCount +
                " " +
                t(
                  attachmentCount === 1
                    ? "ticketList.emailAttachment"
                    : "ticketList.emailAttachments",
                )
              }
            />
          </Grid>
        ) : null}
        {gmailMessageUrl ? (
          <Grid item style={{ marginLeft: "auto" }}>
            <Button
              size="small"
              variant="outlined"
              href={gmailMessageUrl}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<OpenInNewIcon fontSize="small" />}
            >
              {t("ticketList.emailViewInGmail")}
            </Button>
          </Grid>
        ) : null}
      </Grid>
      {!collapsed ? (
        <Grid item xs={12}>
          <Typography variant="subtitle2">{message.subject}</Typography>
          <Typography variant="body2" color="textSecondary">
            {t("ticketList.emailFrom")}: {message.sender || "-"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t("ticketList.emailTo")}: {message.recipient || "-"}
          </Typography>
          <AttachmentChips attachments={message.attachments} />
        </Grid>
      ) : null}
    </>
  );
};

const MessageList = ({
  collapsedHeaders,
  messages,
  expandedMessages,
  t,
  toggleMessageExpanded,
  toggleMessageHeader,
}) => {
  if (!messages.length) {
    return (
      <Grid item xs={12}>
        <Typography color="textSecondary" style={{ padding: 15 }}>
          {t("ticketList.emailNoMessages")}
        </Typography>
      </Grid>
    );
  }

  return messages.map((message) => (
    <Grid
      item
      xs={12}
      key={getMessageKey(message)}
      style={{
        border: "1px solid #d0d7de",
        borderRadius: 4,
        padding: 15,
        backgroundColor: message.direction === "sent" ? "#eef7ff" : "#fffdf5",
      }}
    >
      <Grid container spacing={1}>
        <MessageHeader
          collapsed={Boolean(collapsedHeaders[getMessageKey(message)])}
          message={message}
          t={t}
          onToggle={() => toggleMessageHeader(message)}
        />
        <Grid item xs={12}>
          <MessageBody
            expanded={Boolean(expandedMessages[getMessageKey(message)])}
            message={message}
            t={t}
            onToggle={() => toggleMessageExpanded(message)}
          />
        </Grid>
      </Grid>
    </Grid>
  ));
};

const MessageBody = ({ expanded, message, onToggle, t }) => {
  var content = getMessagePreviewContent(message, expanded);

  if (content.type === "html") {
    return (
      <>
        {content.hasTrimmedContent ? (
          <Button
            size="small"
            variant="outlined"
            onClick={onToggle}
            style={{ marginBottom: 8 }}
          >
            {expanded
              ? t("ticketList.emailShowNewContentOnly")
              : t("ticketList.emailShowFull")}
          </Button>
        ) : null}
        <iframe
          title={"email-message-" + (message.id || message.gmailMessageId)}
          sandbox=""
          srcDoc={getHtmlDocument(getRenderableHtml(content.value))}
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #d0d7de",
            borderRadius: 4,
            height: 360,
            width: "100%",
          }}
        />
      </>
    );
  }

  return (
    <>
      {content.hasTrimmedContent ? (
        <Button
          size="small"
          variant="outlined"
          onClick={onToggle}
          style={{ marginBottom: 8 }}
        >
          {expanded
            ? t("ticketList.emailShowNewContentOnly")
            : t("ticketList.emailShowFull")}
        </Button>
      ) : null}
      <Typography style={{ whiteSpace: "pre-wrap" }}>
        {content.value}
      </Typography>
    </>
  );
};

const SignaturePreview = ({ ticket, t }) => {
  const [expanded, setExpanded] = useState(false);
  var signatureLines = getEmailSignatureLines(ticket);

  return (
    <div
      style={{
        border: "1px solid #d0d7de",
        borderRadius: 4,
        marginTop: 8,
        padding: 12,
        backgroundColor: "#ffffff",
      }}
    >
      <Grid container alignItems="center" spacing={1}>
        <Grid item>
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Grid>
        <Grid item>
          <Typography variant="caption" color="textSecondary">
            {t("ticketList.emailSignature")}
          </Typography>
        </Grid>
      </Grid>
      {expanded ? (
        <>
          <Typography
            variant="body2"
            style={{ marginTop: 8, whiteSpace: "pre-wrap" }}
          >
            {signatureLines.join("\n")}
          </Typography>
          <img
            src={getSignatureLogoPreviewUrl()}
            alt="CTN"
            style={{
              display: "block",
              marginTop: 8,
              maxWidth: 120,
              height: "auto",
            }}
          />
        </>
      ) : null}
    </div>
  );
};

export default function EmailMessages({ selectedTicket }) {
  const ref = useRef();
  const attachmentInputRef = useRef();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [expandedMessages, setExpandedMessages] = useState({});
  const [collapsedHeaders, setCollapsedHeaders] = useState({});
  const [compose, setCompose] = useState(getEmptyCompose());
  const [attachmentFiles, setAttachmentFiles] = useState([]);

  const ticketId = selectedTicket?.id;
  const sortedMessages = useMemo(() => getSortedMessages(messages), [messages]);
  const latestThreadMessage = useMemo(
    () => getLatestThreadMessage(sortedMessages),
    [sortedMessages],
  );
  const hasGmailThread = Boolean(latestThreadMessage?.gmailThreadId);
  const replyRecipient = useMemo(
    () => getReplyRecipient(selectedTicket, sortedMessages),
    [selectedTicket, sortedMessages],
  );
  const replySubject = useMemo(
    () => getReplySubject(selectedTicket, sortedMessages),
    [selectedTicket, sortedMessages],
  );
  const newThreadSubject = useMemo(
    () => getNewThreadSubject(selectedTicket),
    [selectedTicket],
  );
  const messageSubject = String(
    hasGmailThread ? replySubject : compose.subject || "",
  ).trim();

  useEffect(() => {
    if (!ticketId) {
      setMessages([]);
      setExpandedMessages({});
      setCollapsedHeaders({});
      setCompose(getEmptyCompose());
      setAttachmentFiles([]);
      return;
    }

    setExpandedMessages({});
    setCollapsedHeaders({});
    setCompose(getEmptyCompose());
    setAttachmentFiles([]);
    setLoading(true);
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        ticket_id: ticketId,
      }),
    };

    fetch(server + "/get_ticket_email_messages", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          var loadedMessages = result.emailMessages || [];
          setMessages(loadedMessages);
          setCollapsedHeaders(getCollapsedHeaders(loadedMessages));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dispatch, ticketId]);

  useEffect(() => {
    if (hasGmailThread || compose.subject || !newThreadSubject) {
      return;
    }
    setCompose((current) => ({
      ...current,
      subject: newThreadSubject,
    }));
  }, [compose.subject, hasGmailThread, newThreadSubject]);

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTo(0, ref.current.scrollHeight);
    }
  }, [sortedMessages.length]);

  const updateCompose = (key, value) => {
    setCompose((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const toggleMessageExpanded = (message) => {
    var key = getMessageKey(message);

    setExpandedMessages((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const toggleMessageHeader = (message) => {
    var key = getMessageKey(message);

    setCollapsedHeaders((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const showAttachmentSizeError = () => {
    dispatch(
      showPopupSnackbar({
        error: true,
        message: t("ticketList.emailAttachmentTooLarge", {
          maxSize: getFileSizeLabel(MAX_EMAIL_ATTACHMENT_BYTES),
        }),
      }),
    );
  };

  const handleAttachmentChange = (event) => {
    var selectedFiles = Array.from(event.target.files || []);
    var nextFiles = [...attachmentFiles, ...selectedFiles];

    if (!selectedFiles.length) {
      return;
    }
    if (getFileTotalSize(nextFiles) > MAX_EMAIL_ATTACHMENT_BYTES) {
      showAttachmentSizeError();
      event.target.value = null;
      return;
    }
    setAttachmentFiles(nextFiles);
    event.target.value = null;
  };

  const removeAttachmentFile = (index) => {
    setAttachmentFiles((current) =>
      current.filter((attachment, attachmentIndex) => attachmentIndex !== index),
    );
  };

  const sendMessage = async () => {
    var signedBodyText;
    var signedBodyHtml;
    var signatureLogoAttachment;
    var signatureLogoUrl;
    var selectedAttachments;
    var attachments;
    var response;
    var result;

    if (
      !selectedTicket?.id ||
      !replyRecipient ||
      !messageSubject ||
      !compose.bodyText ||
      sending
    ) {
      return;
    }

    if (getFileTotalSize(attachmentFiles) > MAX_EMAIL_ATTACHMENT_BYTES) {
      showAttachmentSizeError();
      return;
    }

    setSending(true);
    try {
      selectedAttachments = await Promise.all(
        attachmentFiles.map((file) => getFileAttachment(file)),
      );
      signatureLogoAttachment = await getSignatureLogoAttachment();
      signatureLogoUrl =
        getExternalSignatureLogoUrl() ||
        (signatureLogoAttachment ? SIGNATURE_LOGO_CID : "");
      signedBodyText = getSignedMessageBody(compose.bodyText, selectedTicket);
      signedBodyHtml = getSignedMessageHtml(
        compose.bodyText,
        selectedTicket,
        signatureLogoUrl,
      );
      attachments = [
        ...selectedAttachments,
        ...(signatureLogoAttachment ? [signatureLogoAttachment] : []),
      ];

      response = await fetch(server + "/send_gmail_message", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: getToken(),
          ticketId: selectedTicket.id,
          ContactId: selectedTicket.ContactId,
          CompanyId: selectedTicket.CompanyId,
          recipient: replyRecipient,
          subject: messageSubject,
          bodyText: signedBodyText,
          bodyHtml: signedBodyHtml,
          attachments,
          gmailThreadId: latestThreadMessage?.gmailThreadId,
          inReplyTo: latestThreadMessage?.rfcMessageId,
          references: getReferences(latestThreadMessage),
        }),
      });
      result = await response.json();
      requestErrorHandler(result, dispatch);
      if (!result.error && result.emailmessage) {
        setMessages((current) => [
          ...current,
          {
            ...result.emailmessage,
            bodyText: result.emailmessage.bodyText || signedBodyText,
            bodyHtml: result.emailmessage.bodyHtml || signedBodyHtml,
            User: {
              fullName: getUser().name,
            },
          },
        ]);
        setCollapsedHeaders((current) => ({
          ...current,
          [getMessageKey(result.emailmessage)]: true,
        }));
        setCompose((current) => ({
          ...current,
          bodyText: "",
        }));
        setAttachmentFiles([]);
        dispatch(
          showPopupSnackbar({
            error: false,
            message: t("ticketList.emailSentNotification"),
          }),
        );
      }
    } catch (error) {
      dispatch(
        showPopupSnackbar({
          error: true,
          message: error.message || t("ticketList.emailSendFailed"),
        }),
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid
        item
        xs={12}
        container
        spacing={2}
        ref={ref}
        style={{
          maxHeight: 420,
          overflowY: "auto",
          border: "1px solid #d0d7de",
          borderRadius: 4,
          padding: 10,
          backgroundColor: "#f6f8fa",
        }}
      >
        {loading ? (
          <Grid item xs={12}>
            <CircularProgress />
          </Grid>
        ) : (
          <MessageList
            collapsedHeaders={collapsedHeaders}
            expandedMessages={expandedMessages}
            messages={sortedMessages}
            t={t}
            toggleMessageExpanded={toggleMessageExpanded}
            toggleMessageHeader={toggleMessageHeader}
          />
        )}
      </Grid>
      {!hasGmailThread ? (
        <Grid item xs={12} style={{ marginTop: 5 }}>
          <TextField
            fullWidth
            variant="outlined"
            label={t("ticketList.emailSubject")}
            value={compose.subject}
            onChange={(event) => updateCompose("subject", event.target.value)}
          />
        </Grid>
      ) : null}
      <Grid item xs={12} style={{ marginTop: 5 }}>
        <TextField
          fullWidth
          multiline
          rows={5}
          variant="outlined"
          label={t("ticketList.emailMessageLabel")}
          value={compose.bodyText}
          onChange={(event) => updateCompose("bodyText", event.target.value)}
        />
        <SignaturePreview ticket={selectedTicket} t={t} />
        <Grid container spacing={1} alignItems="center" style={{ marginTop: 8 }}>
          <Grid item>
            <input
              ref={attachmentInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={handleAttachmentChange}
            />
            <Button
              variant="outlined"
              startIcon={<AttachFileIcon />}
              disabled={sending}
              onClick={() => {
                if (attachmentInputRef.current) {
                  attachmentInputRef.current.click();
                }
              }}
            >
              {t("ticketList.emailAddAttachment")}
            </Button>
          </Grid>
          <Grid item>
            <Typography variant="caption" color="textSecondary">
              {getFileSizeLabel(getFileTotalSize(attachmentFiles))} /{" "}
              {getFileSizeLabel(MAX_EMAIL_ATTACHMENT_BYTES)}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <AttachmentChips
              attachments={attachmentFiles}
              onRemove={sending ? undefined : removeAttachmentFile}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Button
          color="primary"
          variant="contained"
          startIcon={<SendIcon />}
          disabled={
            sending || !replyRecipient || !messageSubject || !compose.bodyText
          }
          onClick={sendMessage}
        >
          {sending ? t("ticketList.emailSending") : t("send")}
        </Button>
      </Grid>
    </Grid>
  );
}
